
import React, { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { TutorialCard } from '@/components/tutorials/TutorialCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';
import { getAllTutorials, getAllCategories, getTutorialsByCategory } from '@/utils/sample-data';

export default function TutorialsPage() {
  const allTutorials = getAllTutorials();
  const allCategories = getAllCategories();
  const difficultyLevels = ['beginner', 'intermediate', 'advanced'];
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);
  
  // Filter tutorials based on search term, category and difficulty
  const filteredTutorials = allTutorials.filter(tutorial => {
    const matchesSearch = !searchTerm || 
      tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tutorial.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tutorial.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      
    const matchesCategory = !selectedCategory || 
      allCategories.find(c => c.id === selectedCategory)?.tutorialIds.includes(tutorial.id);
    const matchesDifficulty = !selectedDifficulty || tutorial.difficulty === selectedDifficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });
  
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(null);
    setSelectedDifficulty(null);
  };
  
  const hasActiveFilters = searchTerm || selectedCategory || selectedDifficulty;

  return (
    <MainLayout showSidebar>
      <div className="container py-16 px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold">All Tutorials</h1>
            <p className="text-muted-foreground mt-2">Browse our collection of technical tutorials</p>
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input 
              placeholder="Search tutorials..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {allCategories.map(category => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(
                selectedCategory === category.id ? null : category.id
              )}
            >
              {category.name}
            </Button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {difficultyLevels.map(level => (
            <Button
              key={level}
              variant={selectedDifficulty === level ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedDifficulty(
                selectedDifficulty === level ? null : level
              )}
            >
              {level.charAt(0).toUpperCase() + level.slice(1)}
            </Button>
          ))}
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="ml-auto"
            >
              <X className="h-4 w-4 mr-2" />
              Clear filters
            </Button>
          )}
        </div>

        {filteredTutorials.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTutorials.map(tutorial => (
              <TutorialCard key={tutorial.id} tutorial={tutorial} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-lg font-medium">No tutorials match your filters</h3>
            <p className="text-muted-foreground mt-2">Try adjusting your search or filters</p>
            <Button onClick={clearFilters} variant="outline" className="mt-4">
              Clear all filters
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
